import { useRef, useState } from "react";
import { EyeOff, Forward, Loader2, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../connect/api";

/** Telegram-style delete: the CEO can delete for everyone (both sides, instantly); staff can only hide on their own side. */
export const DeleteSheet = ({ title, preview, isCeo, onEveryone, onMe, onForward, onClose }) => {
  const [busy, setBusy] = useState("");
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); onClose(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="delete-sheet">
      <div className="w-full max-w-md bg-[#0e1626] border border-white/10 rounded-t-3xl sm:rounded-3xl nxc-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-white/10">
          <Trash2 size={16} className="text-red-300" /><span className="font-semibold text-slate-100 flex-1">{title}</span>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label="Close" data-testid="delete-sheet-close"><X size={16} /></button>
        </div>
        {preview && <div className="px-4 py-2.5 text-xs text-slate-400 border-b border-white/5 line-clamp-2">“{preview}”</div>}
        <div className="p-2 space-y-1">
          {onForward && (
            <button onClick={() => { onClose(); onForward(); }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 text-left text-sm text-slate-100" data-testid="delete-sheet-forward"><Forward size={16} className="text-amber-300" /> Forward…</button>
          )}
          {isCeo && onEveryone && (
            <button onClick={() => run("everyone", onEveryone)} disabled={!!busy} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-red-500/10 text-left" data-testid="delete-for-everyone">
              {busy === "everyone" ? <Loader2 size={16} className="animate-spin text-red-300" /> : <Users size={16} className="text-red-300" />}
              <span className="flex-1"><span className="block text-sm font-medium text-red-300">Delete for everyone</span><span className="block text-[11px] text-slate-500">Removed from your side and the staff member's side immediately</span></span>
            </button>
          )}
          <button onClick={() => run("me", onMe)} disabled={!!busy} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 text-left" data-testid="delete-for-me">
            {busy === "me" ? <Loader2 size={16} className="animate-spin" /> : <EyeOff size={16} className="text-slate-300" />}
            <span className="flex-1"><span className="block text-sm font-medium text-slate-100">Delete for me</span><span className="block text-[11px] text-slate-500">{isCeo ? "Hidden only on your side" : "Hidden on your side only — the other person still sees it"}</span></span>
          </button>
          <button onClick={onClose} className="w-full p-3 rounded-xl hover:bg-white/5 text-sm text-slate-400" data-testid="delete-sheet-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
};

/** Long-press (touch) or right-click opens the actions sheet for a message bubble. */
export const useLongPress = (onOpen, ms = 480) => {
  const timer = useRef(null);
  const start = (m) => (e) => { if (e.pointerType === "mouse" && e.button !== 0) return; clearTimeout(timer.current); timer.current = setTimeout(() => onOpen(m), ms); };
  const cancel = () => clearTimeout(timer.current);
  return (m) => ({ onPointerDown: start(m), onPointerUp: cancel, onPointerLeave: cancel, onPointerCancel: cancel, onPointerMove: cancel, onContextMenu: (e) => { e.preventDefault(); cancel(); onOpen(m); } });
};

export const deleteMessage = (cid, mid, scope) => api.delete(`/conversations/${cid}/messages/${mid}`, { params: { scope } });
export const deleteConversation = (cid, scope) => api.delete(`/conversations/${cid}`, { params: { scope } });
